import Template from "../../components/Template";
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '../../context/NavigationContext';
import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import api from "../../services/api";
import config from "../../config";
import { formatDateWithHour, formatDateWithoutHour } from "../../utils/date";
import * as XLSX from "xlsx";
import AlertDialog from "../../components/AlertDialog";
import { Link } from "react-router-dom";
import { parseCurrencyToFloat } from "../../utils/utils";
import { maskCurrency } from "../../utils/masks";
import usePermission from "../../hooks/usePermission";
import useListFilterState from "../../hooks/useListFilterState";
import SincronizacaoManualNota from "../../components/SincronizacaoManualNota";

import useRequisicaoMaisRecente from '../../hooks/useRequisicaoMaisRecente';
import { lerErroDeDownload } from '../../utils/erroDownload';
// Componente memoizado para linha da tabela - evita re-renders desnecessários
// A seleção vem por prop (e não presa dentro do formatter) para que só a linha
// clicada re-renderize: com centenas de notas na tela, refazer todas as linhas a
// cada clique no checkbox deixaria a listagem lenta.
const TableRow = memo(({ doc, columns, selecionada, onAlternarSelecao }) => {
    return (
        <tr key={doc.id}>
            {columns
                .filter((col) => col.visible)
                .map((col) => (
                    <td key={`${doc.id}-${col.id}`} className="px-4 py-2 border border-slate-200 text-slate-700 text-base">
                        {col.formatter(doc[col.id], doc, { selecionada, onAlternarSelecao })}
                    </td>
                ))}
        </tr>
    );
}, (prevProps, nextProps) => {
    // Comparação por referência - detecta novos objetos criados por spread (ex: após vincular obra)
    return prevProps.doc === nextProps.doc &&
           prevProps.columns === nextProps.columns &&
           prevProps.selecionada === nextProps.selecionada &&
           prevProps.onAlternarSelecao === nextProps.onAlternarSelecao;
});

const NotasFiscais = () => {
    const navigate = useNavigate();
    const [fornecedores, setFornecedores] = useState([]);
    const podeVisualizarNota = usePermission("DOWNLOAD_NOTA");
    const [obras, setObras] = useState([]);
    const { updateMenu } = useNavigation();
    const [notasFiscais, setNotasFiscais] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null); // Estado para o documento selecionado
    const [showDialog, setShowDialog] = useState(false); // Estado para controlar o modal    
    const [selectedPdf, setSelectedPdf] = useState("");
    const [currentPage, setCurrentPage] = useState(1); // Página atual
    const [totalRecords, setTotalRecords] = useState(0); // Total de registros    
    const menuRef = useRef(null);
    const menuFiltrarRef = useRef(null);
    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [selectedNotas, setSelectedNotas] = useState([]); // IDs selecionados
    const [baixandoNotas, setBaixandoNotas] = useState(false);
    const [clientes, setClientes] = useState([]);

    const { filters, setFilters, searchTerm, setSearchTerm, isSearchActive, setIsSearchActive, pageSize, setPageSize, hasRestoredFilters, clearPersistedState } = useListFilterState('notas-fiscais', {
        fornecedor: "",
        obra: "",
        numero: "",
        status: "",
        dataEmissaoInicial: "",
        dataEmissaoFinal: "",
        cliente: "",
    });
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false); // Estado de carregamento

    // Debounce para pesquisa - evita chamadas excessivas
    const debounceTimerRef = useRef(null);
    // Ref para medir tempo total incluindo renderização
    const renderStartTimeRef = useRef(null);
    // Ref para medir apenas tempo de renderização (após setState)
    const preRenderTimeRef = useRef(null);

    function maskCurrencyLocal(valueInCentavos) {
        const valueInReais = valueInCentavos / 100;
        return valueInReais.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
        });
    }

    useEffect(() => {
        // Quando 'obras' chega, reconstruímos o array de colunas.
        setColumns([
            {
                id: "select",
                label: "", // sem label
                visible: true,
                // O estado vem em `extras`: ler `selectedNotas` aqui dentro congelava
                // a lista vazia da primeira renderização e o checkbox nunca marcava.
                headerFormatter: (extras = {}) => (
                    <input
                        type="checkbox"
                        aria-label="Selecionar todas as notas da página"
                        title="Selecionar todas as notas da página"
                        checked={!!extras.todasSelecionadas}
                        ref={(el) => { if (el) el.indeterminate = !!extras.selecaoParcial; }}
                        onChange={extras.onAlternarTodas}
                    />
                ),
                formatter: (value, doc, extras = {}) => (
                    <input
                        type="checkbox"
                        aria-label={`Selecionar nota ${doc?.id}`}
                        checked={!!extras.selecionada}
                        onChange={() => extras.onAlternarSelecao?.(doc?.id)}
                    />
                ),
            },
            {
                id: "id",
                label: "ID",
                visible: true,
                formatter: (value) => value
            },
            {
                id: "numero",
                label: "Numero",
                visible: true,
                formatter: (value) => value,
            },
            {
                id: "fornecedor",
                label: "Fornecedor",
                visible: true,
                formatter: (value, doc) => doc.fornecedorFormatted || "-",
            },
            {
                id: "dataEmissao",
                label: "Data",
                visible: true,
                formatter: (value, doc) => doc.dataEmissaoFormatted || "",
            },
            {
                id: "valorBruto",
                label: "Valor Bruto",
                visible: true,
                formatter: (value, doc) => doc.valorBrutoFormatted || "",
            },

            {
                id: "valor",
                label: "Valor Líquido",
                visible: true,
                formatter: (value, doc) => doc.valorFormatted || "",
            },
            {
                id: "status",
                label: "Status",
                visible: true,
                formatter: (value) => value || "-",
            },
            {
                id: "obra",
                label: "Obra",
                visible: true,
                formatter: (value, doc) => {
                    // Para NF SEM obra vinculada, oculta obras concluídas (não elegíveis para novo vínculo).
                    // Para NF já vinculada, mostra todas (para manter visível a obra atual mesmo se concluída).
                    const temObraVinculada = !!doc?.obra?.id;
                    const obrasElegiveis = temObraVinculada
                        ? obras
                        : obras.filter((o) => (o.status?.nome || "").toLowerCase() !== "concluído");
                    return (
                        <select
                            value={doc?.obra?.id || ""}
                            onChange={(e) => handleChangeObra(doc.id, e.target.value)}
                            className="border border-slate-300 text-sm rounded"
                        >
                            <option value="">(Sem obra)</option>
                            {obrasElegiveis.map((obra) => (
                                <option key={obra.id} value={obra.id}>
                                    {obra.nomeProjeto.toUpperCase()}
                                </option>
                            ))}
                        </select>
                    );
                },
            },
            {
                id: "pdf",
                label: "PDF",
                visible: true,
                formatter: (value) => (
                    podeVisualizarNota && (
                        <div
                            className="w-10 h-10 relative cursor-pointer flex justify-center items-center"
                            onClick={() => handleShowPdf(value)}
                        >
                            <img
                                src="images/icon/pdf.png"
                                className="w-10 h-10"
                                title="Ver"
                                alt="Ver"
                            />
                        </div>
                    )
                ),
            },
            {
                id: "xml",
                label: "XML",
                visible: true,
                formatter: (value) => (
                    <div
                        className="w-10 h-10 relative cursor-pointer flex justify-center items-center"
                        onClick={() => handleDownloadXml(value)}
                    >
                        <img
                            src="images/icon/xml.png"
                            className="w-10 h-10"
                            title="Ver"
                            alt="Ver"
                        />
                    </div>
                ),
            },
        ]);
    }, [obras]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [fornecedoresRes, obrasRes, clientesRes] = await Promise.all([
                    api.get("/fornecedores/todos"),
                    api.get("/obras/listar/todas"),
                    api.get("/clientes/todos")
                ]);
                setFornecedores(fornecedoresRes.data);
                setObras(obrasRes.data);
                setClientes(clientesRes.data);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (hasRestoredFilters()) {
            applyFilters();
            return;
        }
        fetchNotasFiscais(currentPage, pageSize);
    }, [currentPage]);

    // Otimização: memoizar filteredDocumentos para evitar recálculos desnecessários
    const filteredDocumentos = useMemo(() => {
        console.time('🔍 Filtro de busca local');
        if (searchTerm.trim() === "") {
            console.log(`📋 Exibindo todos os ${notasFiscais.length} registros (sem filtro de busca)`);
            console.timeEnd('🔍 Filtro de busca local');
            return notasFiscais;
        }
        const filtered = notasFiscais.filter((doc) =>
            doc.nomeDocumento?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        console.log(`🔍 Filtro de busca aplicado: ${filtered.length} de ${notasFiscais.length} registros`);
        console.timeEnd('🔍 Filtro de busca local');
        return filtered;
    }, [searchTerm, notasFiscais]);

    // Consulta O(1) na hora de renderizar cada linha: com 500+ notas, um
    // `includes` por linha a cada clique pesava na tela.
    const idsSelecionados = useMemo(() => new Set(selectedNotas), [selectedNotas]);

    // Quantas das notas visíveis estão marcadas — alimenta o checkbox do cabeçalho.
    const selecaoDaPagina = useMemo(() => {
        const marcadas = filteredDocumentos.filter((doc) => idsSelecionados.has(doc.id)).length;
        return {
            todasSelecionadas: filteredDocumentos.length > 0 && marcadas === filteredDocumentos.length,
            selecaoParcial: marcadas > 0 && marcadas < filteredDocumentos.length,
        };
    }, [filteredDocumentos, idsSelecionados]);

    // Marca ou desmarca todas as notas visíveis, preservando o que já estava
    // selecionado em outras páginas.
    const alternarSelecaoDaPagina = useCallback(() => {
        const idsDaPagina = filteredDocumentos.map((doc) => doc.id).filter((id) => Number.isInteger(id));
        setSelectedNotas((prev) => {
            const todasMarcadas = idsDaPagina.length > 0 && idsDaPagina.every((id) => prev.includes(id));
            if (todasMarcadas) return prev.filter((id) => !idsDaPagina.includes(id));
            return [...new Set([...prev, ...idsDaPagina])];
        });
    }, [filteredDocumentos]);

    // Otimização: pré-processar dados para evitar formatações repetidas no render
    const preprocessData = useCallback((records) => {
        console.time('⏱️ Pré-processamento de dados');
        console.log(`📦 Iniciando pré-processamento de ${records.length} registros...`);
        
        const processed = records.map(record => ({
            ...record,
            // Pré-formatar textos
            fornecedorFormatted: ((record.fornecedor?.razaoSocial || record.fornecedor?.nomeFornecedor || '') + " - " + (record.fornecedor?.cpfCnpj || '')).toUpperCase(),
            // Pré-formatar datas
            dataEmissaoFormatted: record.dataEmissao ? formatDateWithoutHour(record.dataEmissao) : "",
            // Pré-formatar valores monetários
            valorBrutoFormatted: record.valorBruto ? parseFloat(record.valorBruto).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : "",
            valorFormatted: record.valor ? parseFloat(record.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : "",
        }));
        
        console.timeEnd('⏱️ Pré-processamento de dados');
        console.log(`✅ ${records.length} registros pré-processados com sucesso!`);
        return processed;
    }, []);

    // Otimização: usar useCallback para evitar recriação da função
    // Filtros efetivamente aplicados pelo botao "Filtrar". Sao reenviados em toda
    // troca de pagina e de "resultados por pagina": antes a paginacao refazia a
    // busca sem filtro nenhum e a lista voltava silenciosamente ao conjunto completo.
    const appliedFiltersRef = useRef({});

    // Descarta respostas de filtros que já foram superados por outro mais novo:
    // sem isto, a resposta lenta de um filtro anterior sobrescrevia a tela.
    const requisicaoMaisRecente = useRequisicaoMaisRecente();

    const fetchNotasFiscais = useCallback(async (page, size) => {
        const operationId = `fetch-page-${page}-size-${size}`;
        console.log(`\n🚀 =======================================================`);
        console.log(`🚀 INICIANDO BUSCA - Página ${page} | Registros por página: ${size}`);
        console.log(`🚀 =======================================================`);
        
        // Aviso de performance
        if (size > 1000) {
            console.warn(`⚠️ AVISO DE PERFORMANCE: ${size} registros podem causar lentidão na renderização!`);
            console.warn(`💡 RECOMENDAÇÃO: Use no máximo 500 registros ou implemente virtualização (react-window)`);
        }
        
        console.time(`⏱️ Tempo total da operação`);
        
        // Marca o início para medir tempo até renderização completa
        renderStartTimeRef.current = performance.now();
        
        try {
            setLoading(true);
            
            // Medir tempo da requisição API
            console.time('🌐 Requisição API');
            const apiStartTime = performance.now();
            
            const params = new URLSearchParams({ page, pageSize: size });
            Object.entries(appliedFiltersRef.current).forEach(([chave, valor]) => {
                if (valor !== undefined && valor !== null && valor !== "") params.set(chave, valor);
            });
            const idRequisicao = requisicaoMaisRecente.iniciar();
            const response = await api.get(`/notasfiscais?${params.toString()}`);
            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;

            
            const apiEndTime = performance.now();
            console.timeEnd('🌐 Requisição API');
            console.log(`📊 API retornou ${response.data.records.length} registros de ${response.data.total} total`);
            console.log(`⚡ Latencia da API: ${(apiEndTime - apiStartTime).toFixed(2)}ms`);
            
            // Pré-processar dados antes de salvar no state
            const processedData = preprocessData(response.data.records);
            
            // Medir tempo de atualização do state
            console.time('📦 Atualização do State');
            preRenderTimeRef.current = performance.now(); // Captura tempo antes da renderização
            setNotasFiscais(processedData);
            setTotalRecords(response.data.total);
            setPageSize(size);
            console.timeEnd('📦 Atualização do State');
            
            // Aguarda o React terminar de renderizar antes de remover o loading
            console.log('⏳ Aguardando renderização do React (100ms)...');
            setTimeout(() => {
                setLoading(false);
                console.timeEnd(`⏱️ Tempo total da operação`);
                console.log(`✅ BUSCA CONCLUÍDA COM SUCESSO!`);
                console.log(`🏁 =======================================================\n`);
            }, 100);
        } catch (error) {
            console.timeEnd(`⏱️ Tempo total da operação`);
            console.error("❌ Erro ao carregar dados:", error);
            console.log(`❌ =======================================================\n`);
            setLoading(false);
        }
    }, [preprocessData]);

    // Otimização: usar useCallback
    const fetchFilteredNotasFiscais = useCallback(async (query) => {
        console.log(`\n🔍 =======================================================`);
        console.log(`🔍 APLICANDO FILTROS`);
        console.log(`🔍 Filtros:`, query);
        console.log(`🔍 =======================================================`);
        console.time(`⏱️ Tempo total com filtros`);
        
        // Marca o início para medir tempo até renderização completa
        renderStartTimeRef.current = performance.now();
        
        try {
            setLoading(true);
            
            // Medir tempo da requisição API
            console.time('🌐 Requisição API (filtrada)');
            const apiStartTime = performance.now();
            
            const params = new URLSearchParams(query).toString();
            const idRequisicao = requisicaoMaisRecente.iniciar();
            const response = await api.get(`/notasfiscais?${params}`);
            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;

            
            const apiEndTime = performance.now();
            console.timeEnd('🌐 Requisição API (filtrada)');
            console.log(`📊 API retornou ${response.data.records.length} registros filtrados de ${response.data.total} total`);
            console.log(`⚡ Latencia da API: ${(apiEndTime - apiStartTime).toFixed(2)}ms`);

            // Pré-processar dados antes de salvar no state
            const processedData = preprocessData(response.data.records);
            
            console.time('📦 Atualização do State (filtrado)');
            preRenderTimeRef.current = performance.now(); // Captura tempo antes da renderização
            setNotasFiscais(processedData);
            setTotalRecords(response.data.total);
            setCurrentPage(1);
            console.timeEnd('📦 Atualização do State (filtrado)');
            
            // Aguarda o React terminar de renderizar antes de remover o loading
            console.log('⏳ Aguardando renderização do React (100ms)...');
            setTimeout(() => {
                setLoading(false);
                console.timeEnd(`⏱️ Tempo total com filtros`);
                console.log(`✅ FILTROS APLICADOS COM SUCESSO!`);
                console.log(`🏁 =======================================================\n`);
            }, 100);
        } catch (error) {
            console.timeEnd(`⏱️ Tempo total com filtros`);
            console.error("❌ Erro ao aplicar filtros:", error);
            console.log(`❌ =======================================================\n`);
            setLoading(false);
        }
    }, [preprocessData]);

    const handleNavigateCadastrar = async (e) => {
        e.preventDefault();
        try {
            navigate('/notasFiscais/cadastrar');
        } catch (err) {
            console.error('Notas Fiscais Error:', err);
            alert(err.response?.data?.msg || 'Erro ao ir para o cadastro');
        }
    };

    const handleCloseDialog = () => {
        setSelectedDoc(null);
        setShowDialog(false);
    };

    const handleShowPdf = (url) => {
        setSelectedPdf(url);
        setShowDialog(true);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Alterna a seleção de uma nota. Antes o handler dependia de `e.target.checked`;
    // como o checkbox nunca chegava a ficar marcado, um segundo clique mandava
    // "marcar" de novo e o mesmo id entrava duas vezes na lista.
    const alternarSelecao = useCallback((notaId) => {
        const id = Number(notaId);
        if (!Number.isInteger(id)) return;
        setSelectedNotas((prev) => (
            prev.includes(id) ? prev.filter((selecionado) => selecionado !== id) : [...prev, id]
        ));
    }, []);

    const downloadSelectedNotas = async () => {
        if (selectedNotas.length === 0) {
            setMessage("Selecione ao menos uma nota para baixar.");
            setShowSuccessDialog(true);
            return;
        }

        setBaixandoNotas(true);
        try {
            // Faz uma requisição para o backend
            // Passando os IDs selecionados, e recebe um ZIP (blob)
            const response = await api.post("/notasfiscais/download", {
                ids: selectedNotas
            }, {
                responseType: 'blob' // importante p/ receber binário
            });

            // Cria um URL para o blob
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);

            // Cria um link 'invisível' e clica nele
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'notasSelecionadas.zip');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Limpando o objeto URL
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao baixar ZIP:', error);
            // Com `responseType: blob` o motivo do erro vem como Blob: sem ler o
            // corpo, qualquer falha virava o mesmo "verifique o console".
            const motivo = await lerErroDeDownload(error, 'Não foi possível baixar as notas selecionadas.');
            setMessage(motivo);
            setShowSuccessDialog(true);
        } finally {
            setBaixandoNotas(false);
        }
    };

    const handleChangeObra = async (notaId, obraId) => {
        try {
            // Se obraId for vazio, significa remover a obra, 
            // ou obraId é um número?
            const obraIdNumber = obraId ? Number(obraId) : null;

            await api.patch(`/notasfiscais/${notaId}/vincularObra`, {
                obraId: obraIdNumber,
            });

            // Opcional: atualizar localmente a lista de notas (para refletir a obra escolhida)
            setNotasFiscais((prevNotas) =>
                prevNotas.map((nf) =>
                    nf.id === notaId
                        ? { ...nf, obra: obras.find((o) => o.id === obraIdNumber) || null }
                        : nf
                )
            );

            alert("Obra vinculada com sucesso!");
        } catch (error) {
            console.error("Erro ao vincular obra à nota:", error);
            alert("Não foi possível vincular obra. Verifique o console.");
        }
    };

    // Otimização: Memoizar cálculos de paginação
    const paginationInfo = useMemo(() => {
        const totalPages = Math.ceil(totalRecords / pageSize);
        const startRecord = totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0;
        const endRecord = Math.min(currentPage * pageSize, totalRecords);

        // Janela de páginas exibidas
        const pageWindowSize = 5;
        const startPage = Math.max(1, currentPage - Math.floor(pageWindowSize / 2));
        const endPage = Math.min(totalPages, startPage + pageWindowSize - 1);

        const pageNumbers = Array.from(
            { length: endPage - startPage + 1 },
            (_, i) => startPage + i
        );

        return { totalPages, startRecord, endRecord, pageNumbers };
    }, [totalRecords, pageSize, currentPage]);

    const { totalPages, startRecord, endRecord, pageNumbers } = paginationInfo;

    // Handlers para sincronização manual
    const handleSincronizacaoSucesso = (mensagem) => {
        setMessage(mensagem);
        setShowSuccessDialog(true);
        // Opcional: recarregar a lista de notas após alguns segundos
        setTimeout(() => {
            fetchNotasFiscais(currentPage, pageSize);
        }, 3000);
    };

    const handleSincronizacaoErro = (mensagem) => {
        setMessage(mensagem);
        setShowSuccessDialog(true);
    };

    const [columns, setColumns] = useState([
        {
            id: "select",
            label: "", // sem label
            visible: true,
            headerFormatter: (extras = {}) => (
                <input
                    type="checkbox"
                    aria-label="Selecionar todas as notas da página"
                    title="Selecionar todas as notas da página"
                    checked={!!extras.todasSelecionadas}
                    ref={(el) => { if (el) el.indeterminate = !!extras.selecaoParcial; }}
                    onChange={extras.onAlternarTodas}
                />
            ),
            formatter: (value, doc, extras = {}) => (
                <input
                    type="checkbox"
                    aria-label={`Selecionar nota ${doc?.id}`}
                    checked={!!extras.selecionada}
                    onChange={() => extras.onAlternarSelecao?.(doc?.id)}
                />
            ),
        },
        {
            id: "id",
            label: "ID",
            visible: true,
            formatter: (value) => (
                <Link to={`/notasfiscais/editar/${value}`} className="text-stone-600 hover:underline">
                    {value}
                </Link>
            ),
        },
        {
            id: "numero",
            label: "Numero",
            visible: true,
            formatter: (value) => value,
        },
        {
            id: "fornecedor",
            label: "Fornecedor",
            visible: true,
            formatter: (value) => value?.nomeFornecedor?.toUpperCase() || value?.razaoSocial?.toUpperCase(),
        },
        {
            id: "dataEmissao",
            label: "Data",
            visible: true,
            formatter: (value) =>
                value ? formatDateWithoutHour(value) : "",
        },
        {
            id: "valorBruto",
            label: "Valor Bruto",
            visible: true,
            formatter: (value) => {
                if (!value) return "";
                // Garante que é número (ou converte string->float)
                const num = parseFloat(value);
                console.log(num);
                // Formata no padrão PT-BR com 2 casas
                return num.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                });
            },
        },

        {
            id: "valor",
            label: "Valor Líquido",
            visible: true,
            formatter: (value) => {
                if (!value) return "";
                // Garante que é número (ou converte string->float)
                const num = parseFloat(value);
                // Formata no padrão PT-BR com 2 casas
                return num.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                });
            },
        },
        {
            id: "obra",
            label: "Obra",
            visible: true,
            formatter: (value, doc) => {
                const temObraVinculada = !!doc?.obra?.id;
                const obrasElegiveis = temObraVinculada
                    ? obras
                    : obras.filter((o) => (o.status?.nome || "").toLowerCase() !== "concluído");
                return (
                    <select
                        value={doc.obra?.id || ""}
                        onChange={(e) => handleChangeObra(doc.id, e.target.value)}
                        className="border border-slate-300 text-sm rounded"
                    >
                        <option value="">(Sem obra)</option>
                        {obrasElegiveis.map((obra) => (
                            <option key={obra.id} value={obra.id}>
                                {obra.nomeProjeto}
                            </option>
                        ))}
                    </select>
                );
            },
        },
        {
            id: "pdf",
            label: "PDF",
            visible: true,
            formatter: (value) => (
                <div
                    className="w-10 h-10 relative cursor-pointer flex justify-center items-center"
                    onClick={() => handleShowPdf(value)}
                >
                    <img
                        src="images/icon/pdf.png"
                        className="w-10 h-10"
                        title="Ver"
                        alt="Ver"
                    />
                </div>
            ),
        },
        {
            id: "xml",
            label: "XML",
            visible: true,
            formatter: (value) => (
                <div
                    className="w-10 h-10 relative cursor-pointer flex justify-center items-center"
                    onClick={() => handleDownloadXml(value)}
                >
                    <img
                        src="images/icon/xml.png"
                        className="w-10 h-10"
                        title="Ver"
                        alt="Ver"
                    />
                </div>
            ),
        },
    ]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuFiltrarOpen, setMenuFiltrarOpen] = useState(false);

    const toggleColumnVisibility = (id) => {
        setColumns((prevColumns) =>
            prevColumns.map((col) =>
                col.id === id ? { ...col, visible: !col.visible } : col
            )
        );
    };

    // Função para fechar o menu ao clicar fora
    const handleClickOutside = (event) => {
        if (
            buttonRef.current &&
            !buttonRef.current.contains(event.target) &&
            buttonFiltrarRef.current &&
            !buttonFiltrarRef.current.contains(event.target)
        ) {
            setMenuOpen(null);
        }
    };

    useEffect(() => {
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);

        };
    }, [menuOpen]);

    // Log de performance para renderização da tabela
    useEffect(() => {
        if (!loading && filteredDocumentos.length > 0 && columns && columns.length > 0) {
            // Aguarda um tick para garantir que o DOM foi atualizado
            requestAnimationFrame(() => {
                const renderEndTime = performance.now();
                const totalRenderTime = renderStartTimeRef.current 
                    ? (renderEndTime - renderStartTimeRef.current).toFixed(2)
                    : 'N/A';
                
                // Tempo apenas da renderização (excluindo API e processamento)
                const pureRenderTime = preRenderTimeRef.current
                    ? (renderEndTime - preRenderTimeRef.current).toFixed(2)
                    : 'N/A';
                
                const totalCells = filteredDocumentos.length * columns.filter(c => c.visible).length;
                const renderTimeMs = parseFloat(totalRenderTime);
                const pureRenderMs = parseFloat(pureRenderTime);
                
                console.log(`\n🎨 ========================================`);
                console.log(`🎨 TABELA RENDERIZADA (DOM ATUALIZADO)`);
                console.log(`🎨 Registros exibidos: ${filteredDocumentos.length}`);
                console.log(`🎨 Colunas visíveis: ${columns.filter(c => c.visible).length}`);
                console.log(`🎨 Total de células: ${totalCells}`);
                console.log(`⏱️ TEMPO TOTAL (API + Processamento + Renderização): ${totalRenderTime}ms`);
                console.log(`⏱️ TEMPO DE RENDERIZAÇÃO PURA (apenas DOM): ${pureRenderTime}ms`);
                
                // Métricas adicionais
                if (pureRenderMs > 0 && totalCells > 0) {
                    const timePerCell = (pureRenderMs / totalCells).toFixed(4);
                    console.log(`📊 Tempo médio por célula: ${timePerCell}ms`);
                }
                
                // Análise de performance
                if (renderTimeMs > 5000) {
                    console.log(`\n🔴 PERFORMANCE CRÍTICA DETECTADA!`);
                    console.log(`🔴 Renderização levou ${(renderTimeMs / 1000).toFixed(1)}s`);
                    console.log(`🔴 Renderização pura (DOM): ${(pureRenderMs / 1000).toFixed(1)}s`);
                    console.log(`💡 RECOMENDAÇÕES:`);
                    console.log(`   1. ⭐ Implementar virtualização (react-window) - Melhoria de 90-95%`);
                    console.log(`   2. 📊 Reduzir registros exibidos para máximo 500`);
                    console.log(`   3. 👁️ Ocultar colunas desnecessárias`);
                    console.log(`   4. 📄 Usar paginação ao invés de "mostrar tudo"`);
                } else if (renderTimeMs > 2000) {
                    console.warn(`⚠️ Performance pode melhorar! Renderização: ${(renderTimeMs / 1000).toFixed(1)}s`);
                    console.warn(`💡 Considere usar virtualização para >1000 registros`);
                }
                
                console.log(`🎨 ========================================\n`);
                
                // Reseta os timestamps
                renderStartTimeRef.current = null;
                preRenderTimeRef.current = null;
            });
        }
    }, [loading, filteredDocumentos.length, columns]);

    const handleMenuClick = (event) => {
        // Impede a propagação do evento para o handleClickOutside
        event.stopPropagation();
    };

    const applyFilters = () => {
        appliedFiltersRef.current = { ...filters };
        setCurrentPage(1);
        const query = {
            ...filters,
            page: 1,
            pageSize,
        };

        fetchFilteredNotasFiscais(query);
    };

    const clearFilters = () => {
        clearPersistedState();
        appliedFiltersRef.current = {};
        setSearchTerm(""); // Limpa o termo de pesquisa
        setCurrentPage(1); // Reinicia a páginação

        setFilters({
            fornecedor: "",
            obra: "",
            numero: "",
            status: "",
            dataEmissaoInicial: "",
            dataEmissaoFinal: "",
            cliente: ""
        });
        fetchNotasFiscais(1, pageSize); // Restaura os dados padrão
    };

    // Otimização: Debounce na pesquisa
    const debouncedSearch = useCallback((searchFilters) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        debounceTimerRef.current = setTimeout(() => {
            const query = {
                ...searchFilters,
                page: 1,
                pageSize,
            };
            fetchFilteredNotasFiscais(query);
        }, 500); // 500ms de debounce
    }, [pageSize, fetchFilteredNotasFiscais]);

    const handleSearch = useCallback(() => {
        appliedFiltersRef.current = { ...filters };
        setCurrentPage(1);
        const query = {
            ...filters,
            page: 1,
            pageSize,
        };
        fetchFilteredNotasFiscais(query);
    }, [filters, pageSize, fetchFilteredNotasFiscais]);

    // Função para exportar a tabela
    const exportToExcel = () => {
        if (notasFiscais.length === 0) {
            setMessage('Não há dados para exportar!')
            setShowSuccessDialog(true);
            return;
        }

        const monetaryColumns = ['valorBruto', 'valor']; // substitua pelos IDs reais

        // Mapeia os dados da tabela para um formato plano
        const dataToExport = notasFiscais.map((doc) => {
            const row = {};
            columns.forEach((col) => {
                if (col.visible && col.id !== 'select' && col.label != 'PDF' && col.label != 'XML') {
                    // Formatters de coluna que devolvem JSX (links, botoes, checkboxes)
                    // viravam "[object Object]" na planilha: nesses casos exporta o valor bruto.
                    let value = col.formatter ? col.formatter(doc[col.id], doc) : doc[col.id];
                    if (value && typeof value === 'object') value = doc[col.id];

                    // Remove formatação monetária (p.ex. 'R$', '.', ',') e converte para número
                    if (monetaryColumns.includes(col.id)) {
                        value = Number(String(value)
                            .replace(/[R$\s.]/g, '')
                            .replace(',', '.')
                        );
                    }

                    row[col.label] = value;
                }
            });
            return row;
        });

        // Cria uma nova planilha do Excel
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);

        // Formata células monetárias explicitamente como números no Excel
        const columnLabels = columns.filter(col => col.visible).map(col => col.label);
        monetaryColumns.forEach((colId) => {
            const colIndex = columnLabels.indexOf(columns.find(c => c.id === colId)?.label);
            if (colIndex >= 0) {
                const columnLetter = XLSX.utils.encode_col(colIndex);
                const range = XLSX.utils.decode_range(worksheet['!ref']);

                for (let i = range.s.r + 1; i <= range.e.r; i++) {
                    const cellAddress = `${columnLetter}${i + 1}`;
                    if (worksheet[cellAddress]) {
                        worksheet[cellAddress].t = 'n';
                        worksheet[cellAddress].z = '#,##0.00';
                    }
                }
            }
        });

        // Cria um novo workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Notas Fiscais");

        // Exporta o arquivo
        XLSX.writeFile(workbook, "notasFiscais.xlsx");
    };

    const handleDownloadXml = (xmlPath) => {
        if (!xmlPath) {
            console.warn("Não existe caminho para o XML.");
            return;
        }
        const url = config.urlArquivo(xmlPath);
        window.open(url, '_blank');
    };

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div className="flex flex-wrap justify-between items-center gap-4">
                    <div className="text-stone-800 text-xl md:text-2xl font-medium font-['Inter']">Notas Fiscais</div>
                    <div className="flex gap-2">
                        <SincronizacaoManualNota 
                            onSuccess={handleSincronizacaoSucesso}
                            onError={handleSincronizacaoErro}
                        />
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                    <div className="flex-grow min-w-0 flex items-center gap-2">
                        <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-tight">Ver</div>
                        <select
                            className={`w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm text-slate-700 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            value={pageSize}
                            onChange={(e) => {
                                const newPageSize = parseInt(e.target.value, 10);
                                setPageSize(newPageSize);
                                setCurrentPage(1);
                                fetchNotasFiscais(1, newPageSize);
                            }}
                            disabled={loading}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={150}>150</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                            <option value={1000}>1000</option>
                            <option value={2000}>2000</option>
                        </select>
                        <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-tight">Resultados</div>
                    </div>
                    <div className="flex flex-wrap gap-2 flex-none w-full md:w-auto">
                        <div ref={buttonRef} className="relative justify-end items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "colunas" ? null : "colunas")}>
                            <div className="justify-start items-center gap-2 flex" >
                                <img className="w-6 h-6 relative " src="images/icon/columns.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Colunas</div>
                            </div>
                            <img className="w-6 h-4 px-1 justify-center items-center flex" src="images/icon/chevron-down.png" />
                            {menuOpen === "colunas" && (
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50"
                                    style={{
                                        zIndex: 9999, // Garante que está acima de outros elementos
                                    }}
                                    onClick={handleMenuClick} // Impede que o clique feche o menu
                                >
                                    <div className="p-4 flex flex-col gap-3">
                                        {columns.map((col) => (
                                            <div
                                                key={col.id}
                                                className="flex items-center gap-2"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={col.id}
                                                    checked={col.visible}
                                                    onChange={() =>
                                                        toggleColumnVisibility(col.id)
                                                    }
                                                />
                                                <label
                                                    htmlFor={col.id}
                                                    className="text-sm text-gray-700"
                                                >
                                                    {col.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div ref={buttonFiltrarRef} className="relative justify-start items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "filtro" ? null : "filtro")} >
                            <div className="justify-start items-center gap-2 flex">
                                <img className="w-8 h-8 px-0.5 py-[3px] justify-center items-center flex" src="images/icon/filter.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Filtrar</div>
                            </div>
                            <img className="w-6 h-4 px-1 justify-center items-center flex" src="images/icon/chevron-down.png" />
                            {/* Menu Suspenso */}
                            {menuOpen === "filtro" && (
                                <div
                                    className="absolute top-full left-0 mt-2 w-[350px] bg-white rounded-md shadow-lg z-50 p-4 border border-gray-300"
                                    style={{ zIndex: 9999 }}
                                    onClick={handleMenuClick} // Impede que o clique feche o menu
                                >
                                    <div className="flex flex-col gap-4">
                                        <div className="self-stretch justify-start items-center gap-2.5 inline-flex">
                                            <div className="grow shrink basis-0 text-stone-800 text-xl font-medium">
                                                Filtrar
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Cliente
                                            </div>
                                            <select
                                                name="cliente"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.cliente}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, cliente: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {clientes.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {s.nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Fornecedor
                                            </div>
                                            <select
                                                name="fornecedor"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.fornecedor}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, fornecedor: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {fornecedores.map((f) => (
                                                    <option key={f.id} value={f.id}>
                                                        {f.nomeFornecedor.toUpperCase() + " - " + f.cpfCnpj}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Obra
                                            </div>
                                            <select
                                                name="obra"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.obra}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, obra: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {obras.map((o) => (
                                                    <option key={o.id} value={o.id}>
                                                        {o.nomeProjeto.toUpperCase()}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Número
                                            </div>
                                            <input
                                                type="text"
                                                name="numero"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                placeholder="Digite o número da nota"
                                                value={filters.numero}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, numero: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Status
                                            </div>
                                            <input
                                                type="text"
                                                name="status"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                placeholder="Digite o status (ex: Autorizada)"
                                                value={filters.status}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, status: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Emissão
                                            </div>
                                            <div className="flex gap-2">
                                                <input
                                                    type="date"
                                                    name="dataEmissaoInicial"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataEmissaoInicial}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, dataEmissaoInicial: e.target.value })
                                                    }
                                                />
                                                <input
                                                    type="date"
                                                    name="dataEmissaoFinal"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataEmissaoFinal}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, dataEmissaoFinal: e.target.value })
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="self-stretch flex items-center justify-between gap-4 mt-4">
                                            <button
                                                className={`px-4 py-2 w-full bg-white rounded-md border border-stone-500 justify-center items-center gap-2 flex ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                onClick={clearFilters}
                                                disabled={loading}
                                            >
                                                <img className="w-4 h-4 relative" src="images/icon/eraser.png" />
                                                <div className="text-stone-500 text-sm font-medium font-['Inter'] leading-normal" >Limpar</div>
                                            </button>
                                            <button
                                                className={`px-4 py-2 w-full bg-stone-600 rounded-md justify-center items-center gap-2 flex ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                onClick={applyFilters}
                                                disabled={loading}
                                            >
                                                <img className="w-4 h-4 relative" src="images/icon/filter-white.png" />
                                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal" >Filtrar</div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                        <div className="justify-start items-center gap-2 flex">
                            {!isSearchActive ? (
                                // Conteúdo original da div "Pesquisar"
                                <div
                                    className="justify-start items-center gap-2 flex cursor-pointer"
                                    onClick={() => setIsSearchActive(true)} // Ativar o estado de pesquisa
                                >
                                    <img className="w-10 h-10 relative" src="images/icon/search.png" alt="Ícone Pesquisar" />
                                    <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Pesquisar</div>
                                </div>
                            ) : (
                                // Conteúdo alternativo ao ativar o estado de pesquisa
                                <div className="h-10 justify-start items-center gap-2 inline-flex">
                                    <div className="h-9 px-3 py-2 bg-white rounded-md border border-slate-300 justify-start items-center flex">
                                        <input
                                            type="text"
                                            placeholder="Pesquisar"
                                            className="w-full text-slate-400 text-base font-normal font-['Inter'] leading-tight focus:outline-none"
                                            value={filters.numero}
                                            onChange={(e) =>
                                                setFilters({ ...filters, numero: e.target.value })
                                            }
                                        />
                                        <img className="w-6 h-6 p-[3.33px] justify-center items-center flex cursor-pointer" src="images/icon/close.png" onClick={() => {
                                            setIsSearchActive(false);
                                        }} />
                                    </div>
                                    <button
                                        className="px-4 py-2 bg-stone-600 rounded-md justify-center items-center gap-2 flex"
                                        onClick={handleSearch}
                                    >
                                        <img className="w-4 h-4 relative" src="images/icon/search-white.png" alt="Ícone Pesquisar" />
                                        <div className="text-white text-sm font-medium font-['Inter'] leading-normal">Pesquisar</div>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="self-stretch flex justify-between gap-4">
                        {usePermission('EXPORTAR_NOTA') && (
                            <button 
                                type='button' 
                                className={`flex px-4 py-2 bg-white/40 rounded-md border border-stone-600 justify-center items-center gap-2 ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                onClick={exportToExcel}
                                disabled={loading}
                            >
                                <img className="w-4 h-4" src="images/icon/upload.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Exportar</div>
                            </button>
                        )}
                        {usePermission('DOWNLOAD_NOTA') && (
                            <button
                                type='button'
                                data-testid="baixar-notas"
                                className={`flex px-4 py-2 bg-white/40 rounded-md border border-stone-600 justify-center items-center gap-2 ${(loading || baixandoNotas) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                onClick={downloadSelectedNotas}
                                disabled={loading || baixandoNotas}
                            >
                                <img className="w-4 h-4" src="images/icon/download-black.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">
                                    {baixandoNotas
                                        ? 'Baixando...'
                                        : `Download Notas${selectedNotas.length > 0 ? ` (${selectedNotas.length})` : ''}`}
                                </div>
                            </button>
                        )}
                    </div>
                </div>

                {/* Loading Indicator */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-stone-600"></div>
                        <p className="text-stone-600 text-lg font-medium">
                            Carregando {pageSize > 100 ? 'muitos' : ''} registros... 
                            {pageSize > 100 && <span className="text-sm block text-slate-500 mt-1">Isso pode levar alguns segundos</span>}
                        </p>
                    </div>
                )}

                {/* Tabela de Dados */}
                {!loading && (
                    <div className="overflow-x-auto md:overflow-x-visible">
                        <table className="min-w-full bg-white border-collapse border border-gray-200">
                            <thead className="bg-stone-200">
                                <tr>
                                    {columns
                                        .filter((col) => col.visible)
                                        .map((col) => (
                                            <th key={col.id} className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">
                                                {col.headerFormatter
                                                    ? col.headerFormatter({
                                                        ...selecaoDaPagina,
                                                        onAlternarTodas: alternarSelecaoDaPagina,
                                                    })
                                                    : col.label}
                                            </th>
                                        ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDocumentos.map((doc) => (
                                    <TableRow
                                        key={doc.id}
                                        doc={doc}
                                        columns={columns}
                                        selecionada={idsSelecionados.has(doc.id)}
                                        onAlternarSelecao={alternarSelecao}
                                    />
                                ))}
                        {totalRecords === 0 && !loading && (
                            <tr>
                                <td
                                    colSpan={columns.filter((col) => col.visible).length || 1}
                                    className="px-4 py-6 border border-slate-200 text-center text-slate-500 text-base"
                                >
                                    Nenhum registro encontrado.
                                </td>
                            </tr>
                        )}
                            </tbody>
                        </table>
                    </div>
                )}
                
                <div className="self-stretch justify-end items-center gap-2.5 inline-flex">
                    <div className="grow shrink basis-0 text-slate-500 text-base font-normal font-['Inter'] leading-[29.60px]">
                        Mostrando de {startRecord} até {endRecord} de {totalRecords}{" "} registros.
                    </div>
                    <div className="justify-start items-start gap-4 flex">
                        <button
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex ${(currentPage === 1 || loading) ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300 cursor-pointer"
                                }`}
                            disabled={currentPage === 1 || loading}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-left.png" />
                        </button>

                        {pageNumbers.map((page) => (
                            <button
                                key={page}
                                className={`w-10 h-10 rounded justify-center items-center flex text-base font-semibold font-['Inter'] ${page === currentPage
                                    ? "bg-stone-600 text-white"
                                    : "bg-gray-200 text-gray-600"
                                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onClick={() => handlePageChange(page)}
                                disabled={loading}
                            >
                                {page}
                            </button>
                        ))}
                        <button className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex 
                        ${(currentPage === totalPages || loading) ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                            disabled={currentPage >= totalPages || totalPages === 0 || loading}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-right.png" />
                        </button>
                    </div>
                </div>
                {showDialog && (
                    <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-10 flex-col justify-start items-end gap-10 inline-flex overflow-hidden">
                        <div className=" rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                            <div className="flex justify-between items-center p-4">
                                <div className="flex items-center gap-4">
                                    <a href={config.urlArquivo(selectedPdf)} download target="_blank">
                                        <img className="w-6 h-6 relative  overflow-hidden" src="images/icon/download.png" />
                                    </a>
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                </div>
                                <img className="w-6 h-6 relative  overflow-hidden cursor-pointer" src="images/icon/x.png" onClick={handleCloseDialog} />
                            </div>
                            <div className="flex-grow flex justify-center items-center p-4 overflow-hidden">
                                <iframe
                                    src={`${config.urlArquivo(selectedPdf)}#toolbar=0`}
                                    className="w-full h-full border-0"
                                    style={{ border: 'none' }}
                                    title="PDF Viewer"
                                />
                            </div>
                        </div>
                    </div>
                )}

                <AlertDialog
                    isVisible={showSuccessDialog}
                    message={message}
                    onClose={() => setShowSuccessDialog(false)}
                />
            </div>
        </Template>
    )
}
export default NotasFiscais;